import { distinctUntilChanged, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Component, Inject, OnDestroy } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { setTheme } from 'ngx-bootstrap/utils';

import { AUTH_SERVICE_TOKEN, AuthFacade } from '@coin-depo-core/auth';

import { CookieService } from '@coin-depo-core/storage';

import { HttpParams } from '@angular/common/http';
import pack from 'package.json';

@Component({
    selector: 'cd-app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnDestroy {
  private destroyed$ = new Subject<void>();

  ACTIVE_TABS = 'ACTIVE_TABS';
  KEY_TIME = 'KEY_TIME';
  TWO_MINUTE = 100 * 60 * 2;
  tabs!: any;
  lastTime!: any;

  constructor(
    @Inject(AUTH_SERVICE_TOKEN) private authService: AuthFacade,
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private cookies: CookieService,
    private activatedRoute: ActivatedRoute,
  ) {
      if (!window.location.pathname.includes('reset-password')) {
        this.initClearIfNeeded();
      }
    
    if (!localStorage.getItem('version')) {
      localStorage.setItem('version', pack.version)
    } else if (localStorage.getItem('version') !== pack.version) {
      localStorage.removeItem('version')
      this.router.navigate([], {
        relativeTo: this.activatedRoute,
        queryParams: {},
        queryParamsHandling: 'merge',
        fragment: undefined,
      });
    }

    setTheme('bs4');
    const params = new HttpParams({ fromString: window.location.search });
    if (params.get('id')) {
      localStorage.setItem('click_id', params.get('id') as string);
    }
    if (params.get('ga_utm_cookie')) {
      localStorage.setItem('ga_utm', params.get('ga_utm_cookie') as string);
    }
    if (this.cookies.get('_kt_subid_cookie')) {
      localStorage.setItem('subid', this.cookies.get('_kt_subid_cookie') as string);
    }
    if (params.get('subid')) {
      localStorage.setItem('subid', params.get('subid') as string);
    }
    if (params.get('cdkey')) {
      localStorage.setItem('cdkey', params.get('cdkey') as string);
    }
    if (this.cookies.get('cdkey')) {
      localStorage.setItem('cdkey', this.cookies.get('cdkey') as string);
    }
    if (params.get('telegram_id')) {
      localStorage.setItem('telegram_id', params.get('telegram_id') as string);
    }
    if (this.cookies.get('telegram_id')) {
      localStorage.setItem('telegram_id', this.cookies.get('telegram_id') as string);
    }
    if (params.get('clickid')) {
      const clickId = {
        clickid: params.get('clickid'),
        campaign: params.get('campaign'),
        trafficsource: params.get('trafficsource'),
        user_id: params.get('user_id'),
      };
      localStorage.setItem(
        'click_id',
        encodeURIComponent(btoa(JSON.stringify(clickId)))
      );
    }
    if (params.get('twclid')) {
      localStorage.setItem('twclid', params.get('twclid') as string);
    }
    this.router.events
      .pipe(
        takeUntil(this.destroyed$),
        distinctUntilChanged(),
        filter((e): e is NavigationEnd => e instanceof NavigationEnd)
      )
      .subscribe((e: NavigationEnd) => {
        if (e.url.startsWith('/auth')) {
          if (!this.authService.isLoggedIn) {
            this.document.querySelector('body')?.classList.add('form-wrapper');
          } else {
            this.document.querySelector('body')?.classList.add('form-wrapper');
            if (e.url.startsWith('/auth/reset-password')) {
            } else {
              this.document
                .querySelector('body')
                ?.classList.remove('form-wrapper');
              this.router.navigate(['/']);
            }
          }
        } else {
          this.document.querySelector('body')?.classList.remove('form-wrapper');

          if (!this.authService.isLoggedIn && !e.url.startsWith('/downtime')) {
            this.router.navigate(['/', 'auth', 'login']);
          }
        }

        if (e.url === '/') {
          document.title =  'CoinDepo-dashboard';
        } else if (e.url.includes('profile')) {
          document.title = `CoinDepo-${e.url.split('/')[2]}`;
        }  else {
          document.title = `CoinDepo-${e.url.split('/')[1]}`;
        }
      });

      this.handleTabs();
      this.updateTab();
      window.addEventListener('beforeunload', this.deleteTab.bind(this));
  }

  handleTabs() {
    this.tabs = JSON.parse(localStorage.getItem(this.ACTIVE_TABS) || '[]');
    const storedTime = localStorage.getItem(this.KEY_TIME);
    this.lastTime = storedTime ? Number(storedTime) : null;

    if (
      this.authService.accessToken &&
      this.tabs.length === 0 &&
      this.lastTime &&
      Date.now() > Number(this.lastTime) + this.TWO_MINUTE
    ) {
      this.authService.logout();
      this.updateTab();
    }
  }

  updateTab() {
    this.tabs = JSON.parse(localStorage.getItem(this.ACTIVE_TABS) || '[]');
    const newTabArr = [...this.tabs, this.generateUUID()];
    localStorage.setItem(this.ACTIVE_TABS, JSON.stringify(newTabArr));
  }

  deleteTab() {
    this.tabs = JSON.parse(localStorage.getItem(this.ACTIVE_TABS) || '[]');
    const newTabArr = this.tabs.slice(1);
    localStorage.setItem(this.ACTIVE_TABS, JSON.stringify(newTabArr));
    localStorage.setItem(this.KEY_TIME, Date.now().toString());
  }

  private generateUUID(): string {
    return 'tab-' + Date.now() + '-' + Math.random().toString(36).substring(2, 11);
  }

  ngOnDestroy(): void {    
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  async clearAllSiteData() {
    try {
      localStorage.clear();
      sessionStorage.clear();
      this.cookies.clearAllCookies();

      if (window.indexedDB) {
        const databases = await indexedDB.databases();
        for (const db of databases) {
          indexedDB.deleteDatabase(db.name!);
        }
      }

      if ('caches' in window) {
        const names = await caches.keys();
        for (const name of names) {
          await caches.delete(name);
        }
      }

      if (navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      }

      const root = await (navigator.storage as any).getDirectory();
      for await (const [name] of (root as any).entries()) {
        await (root as any).removeEntry(name, { recursive: true });
      }
    } catch (e) {
      console.warn("Error clearing site data:", e);
    }
  }

  async initClearIfNeeded() {
    const isCleared = this.cookies.get('cleared1');

    if (!isCleared) {
      await this.clearAllSiteData();

      this.cookies.set('cleared1', 'true', {
        expires: 30,
        path: '/',
        sameSite: 'Lax'
      });

      window.location.reload();
    }
  }
}
